import { safeGet } from '../../../utils/safeGet'
import { get as _get, post as _post } from '../../../utils/fetcher'
import {
  INIT_DEFINITION_SETTINGS,
  INIT_SEARCH_FILTER,
  SCORING_DEFINITION_TYPES,
} from '../../../settings/scoringProfilesSettings'
import { isNearlyEqual } from '../../../utils/formatter'

export default {
  namespaced: true,

  state: {
    loading: false,
    isDuplicate: false,
    profileCoverage: {},
    scoringProfileId: null,
    scoringProfileName: '',
    defaultScoringProfileName: '',
    scoringProfileDescription: '',
    defaultScoringProfileDescription: '',
    isProfileCoverageProvidedByAPI: false,
    // Product picked in the EfficiencyTestsList "create new test" modal, fetched from
    // /api/detail-page/product (not the bare productsSearch row) so the Characteristics sidebar
    // fields are available — { productId, name, issuerCode, referenceDate, characteristics }, where
    // characteristics is the raw sidebar[0].fields bag. Carried into EfficiencyTestsCreate to show
    // the "Prodotto in test" block and, later, to be included in the save request payload.
    selectedProduct: null,
  },

  mutations: {
    setLoading(state, data) {
      state.loading = data
    },

    setIsDuplicate(state, payload) {
      state.isDuplicate = payload
    },

    setProfileCoverage(state, payload) {
      state.profileCoverage = payload
    },

    setIsProfileCoverageProvidedByAPI(state, payload) {
      state.isProfileCoverageProvidedByAPI = payload
    },

    setScoringProfileId(state, payload) {
      state.scoringProfileId = payload
    },

    setScoringProfileName(state, payload) {
      state.scoringProfileName = payload
    },

    setDefaultScoringProfileName(state, payload) {
      state.defaultScoringProfileName = payload
    },

    setDefaultScoringProfileDescription(state, payload) {
      state.defaultScoringProfileDescription = payload
    },

    setScoringProfileDescription(state, payload) {
      state.scoringProfileDescription = payload
    },

    setSelectedProduct(state, payload) {
      state.selectedProduct = payload
    },
  },

  getters: {
    getLoading: (state) => {
      return safeGet(() => state.loading, false)
    },

    getIsDuplicate: (state) => {
      return safeGet(() => state.isDuplicate, false)
    },

    getProfileCoverage: (state) => {
      return safeGet(() => state.profileCoverage, {})
    },

    getIsProfileCoverageProvidedByAPI: (state) => {
      return safeGet(() => state.isProfileCoverageProvidedByAPI, false)
    },

    getScoringProfileId: (state) => {
      return safeGet(() => state.scoringProfileId, null)
    },

    getScoringProfileName: (state) => {
      return safeGet(() => state.scoringProfileName, '')
    },

    getScoringProfileDescription: (state) => {
      return safeGet(() => state.scoringProfileDescription, '')
    },

    getSelectedProduct: (state) => {
      return safeGet(() => state.selectedProduct, null)
    },

    getScoringProfileNameValidation: (state, getters) => {
      const valueName = getters.getScoringProfileName
      const isValidName = typeof valueName === 'string' && valueName.trim() != ''
      return safeGet(() => isValidName, false)
    },

    getScoringProfileDefinitionValidation: (state, getters, rootState, rootGetters) => {
      const scoringDefinition = rootGetters['efficiencyTestsDefinition/getSelectedScoringDefinition']
      const isValidScoringDefinition = scoringDefinition.list.length > 0

      // scoring profile definition weight: max total drivers's weights
      const selectedScoringTypeTotalWeight = rootGetters['efficiencyTestsDefinition/getMaxTotalWeight']
      const epsilon = scoringDefinition.config === SCORING_DEFINITION_TYPES.custom ? 0.0001 : 0.0005
      const isValidScoringDefinitionTotalWeight = isNearlyEqual(selectedScoringTypeTotalWeight, 1, epsilon)
      // scoring profile definition weight: check if custom drivers has 0 value
      const isDriversWeightsGreaterThanZero = rootGetters['efficiencyTestsDefinition/getDriversWeightsGreaterThanZero']

      return {
        isValidScoringDefinition: isValidScoringDefinition,
        isValidScoringDefinitionTotalWeight: isValidScoringDefinitionTotalWeight,
        isDriversWeightsGreaterThanZero: isValidScoringDefinition && isDriversWeightsGreaterThanZero,
      }
    },
  },

  actions: {
    setLoading({ commit }, value) {
      commit('setLoading', value)
    },

    setIsDuplicate({ commit }, value) {
      commit('setIsDuplicate', value)
    },

    setScoringProfileId({ commit }, payload) {
      commit('setScoringProfileId', payload)
    },

    setScoringProfileName({ commit }, payload) {
      commit('setScoringProfileName', payload)
    },

    setScoringProfileDescription({ commit }, payload) {
      commit('setScoringProfileDescription', payload)
    },

    setSelectedProduct({ commit }, payload) {
      commit('setSelectedProduct', payload)
    },

    loadSelectedProduct({ commit }, { issuerCode, name, referenceDate }) {
      return _post('/api/detail-page/product', { issuerCode, name, referenceDate }).then((resp) => {
        const product = {
          productId: resp.productId,
          name: resp.name,
          issuerCode: resp.issuerCode,
          referenceDate: resp.referenceDate,
          // Raw { key, value, type } bag, rendered with the product detail page's own formatting.
          characteristics: (resp.sidebar && resp.sidebar[0] && resp.sidebar[0].fields) || [],
        }
        commit('setSelectedProduct', product)
        return product
      })
    },

    // Same lookup as EfficiencyTestsList.vue's product picker modal (company products → picked row
    // → /api/detail-page/product), driven by a productId. Resolves null when the product is not
    // among the company products.
    loadSelectedProductById({ dispatch }, productId) {
      const payload = {
        Sf: null,
        companyProductTypes: null,
        scoringProfileIds: null,
        Sort: 'name',
        Page: 1,
        Size: 10000,
      }
      return _post('/api/company/productsSearch', payload).then((data) => {
        const products = Array.isArray(data.products) ? data.products : []
        const row = products.find((p) => String(p.productId != null ? p.productId : p.id) === String(productId))
        return row ? dispatch('loadSelectedProduct', row) : null
      })
    },

    setScoringProfileToDefault({ commit, dispatch }) {
      commit('setIsDuplicate', false)
      commit('setScoringProfileId', null)
      commit('setScoringProfileName', '')
      commit('setDefaultScoringProfileName', '')
      commit('setScoringProfileDescription', '')
      commit('setDefaultScoringProfileDescription', '')
      dispatch('efficiencyTestsCoverageDetail/resetCoverageFilters', null, { root: true })
    },

    newScoringProfile({ commit, state, rootGetters, dispatch }) {
      dispatch('setScoringProfileToDefault')
      dispatch('efficiencyTestsFilters/setCurrentFilters', INIT_SEARCH_FILTER(), { root: true })
      dispatch('efficiencyTestsFilters/setPerimeterType', ['0'], { root: true })
      dispatch('efficiencyTestsDefinition/setSelectedWeightType', 1, { root: true })
      dispatch('efficiencyTestsDefinition/setSequenceWeightsDecay', 0.5, { root: true })
      dispatch('efficiencyTestsDefinition/setScoringDefinitionSettings', INIT_DEFINITION_SETTINGS(), {
        root: true,
      })
      dispatch('efficiencyTestsDefinition/setDefaultScoringDefinitionSettings', INIT_DEFINITION_SETTINGS(), {
        root: true,
      })
      dispatch(
        'efficiencyTestsCoverageDetail/setUpProductsList',
        { excludedProducts: [], productsToSave: [] },
        { root: true }
      )
      dispatch('efficiencyTestsCoverageDetail/resetCoverageCheckState', null, { root: true })
      dispatch('efficiencyTestsDefinition/resetSelectedModel', null, { root: true })
      // EfficiencyTests dropped the model-association feature — always custom scoring definition.
      dispatch('efficiencyTestsDefinition/setScoringProfileDefinitionSelectValue', 0, { root: true })
    },

    loadCommonEfficiencyTestsList() {
      return new Promise((resolve, reject) => {
        const payload = {
          Sf: '',
          Page: 1,
          Size: 10000,
          Sort: 'Name',
        }
        _post('/api/efficiencyTests/search', payload)
          .then((data) => {
            resolve(data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    // The results page only needs the test's own name and description (GET /api/efficiencyTests/{id});
    // the editor loads the rest through efficiencyTestsEditor/loadScoringProfile.
    loadScoringProfile({ commit }, params) {
      commit('setLoading', true)
      commit('setIsProfileCoverageProvidedByAPI', true)

      return _get(`/api/efficiencyTests/${params.id}`)
        .then((data) => {
          commit('setScoringProfileId', params.id)
          commit('setScoringProfileName', data.name)
          commit('setDefaultScoringProfileName', data.name)
          commit('setScoringProfileDescription', data.description || '')
          commit('setDefaultScoringProfileDescription', data.description || '')
        })
        .catch((err) => {
          return err
        })
        .finally(() => {
          commit('setLoading', false)
          commit('setIsProfileCoverageProvidedByAPI', false)
        })
    },
    clearAllStoreModules({ dispatch }) {
      dispatch('timeHorizonIntervals/resetTimeHorizonState', null, { root: true })
      dispatch('efficiencyTests/resetState', null, { root: true })
      dispatch('searchFilters/resetState', null, { root: true })
      dispatch('productDetails/resetState', null, { root: true })
      dispatch('efficiencyTestsDetails/resetState', null, { root: true })
      dispatch('efficiencyTestsList/resetState', null, { root: true })
    },
  },
}
